import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Article } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { NewsCard } from "@/components/NewsCard";
import { ArrowLeft, Loader2, Tag } from "lucide-react";
import { useRegion } from "@/contexts/RegionContext";
import { PageContainer } from "@/components/layout/PageContainer";

export default function TagPage() {
  const { tag } = useParams<{ tag: string }>();
  const { selectedRegion } = useRegion();

  const { data: articles = [], isLoading } = useQuery<Article[]>({
    queryKey: ["/api/articles/tag", tag, selectedRegion],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedRegion && selectedRegion !== "ALL") {
        params.append("region", selectedRegion);
      }
      const url = `/api/articles/tag/${tag}${params.toString() ? `?${params}` : ""}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch articles");
      return response.json();
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <PageContainer size="wide">
        <Link href="/">
          <div>
            <Button variant="ghost" className="mb-6" data-testid="button-back">
              <ArrowLeft className="h-4 w-4 mr-2" />
              홈으로
            </Button>
          </div>
        </Link>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Tag className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold">#{tag}</h1>
          </div>
          <p className="text-muted-foreground">
            {articles.length}개의 기사
          </p>
        </div>

        {articles.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground">해당 태그의 기사가 없습니다.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
        )}
    </PageContainer>
  );
}
